import {
  HttpClient,
  SdkResponse,
  transformResponse,
  UserHistoryResponse,
  UserResponse,
  LoginOptions,
} from '@descope/core-js-sdk';
import {
  ProviderTokenResponse,
  AssociatedTenant,
  GenerateEnchantedLinkForTestResponse,
  GenerateMagicLinkForTestResponse,
  GenerateOTPForTestResponse,
  GenerateEmbeddedLinkResponse,
  AttributesTypes,
  UserStatus,
  User,
  CreateOrInviteBatchResponse,
  PatchUserBatchResponse,
  TemplateOptions,
  ProviderTokenOptions,
  UserOptions,
  UserSearchResponse,
  CustomAttribute,
  UserPasskey,
  UserTrustedDevice,
  UserImportResponse,
} from './types';
import { DeliveryMethodForTestUser } from '../types';
import apiPaths from './paths';
import { transformUsersForBatch } from './helpers';

type SearchSort = {
  field: string;
  desc?: boolean;
};

type RolesList = {
  values: string[];
  and?: boolean;
};

type SearchRequest = {
  page?: number;
  limit?: number;
  sort?: SearchSort[];
  text?: string;
  emails?: string[];
  phones?: string[];
  statuses?: UserStatus[];
  roles?: string[];
  tenantIds?: string[];
  customAttributes?: Record<string, AttributesTypes>;
  withTestUser?: boolean;
  testUsersOnly?: boolean;
  ssoAppIds?: string[];
  loginIds?: string[];
  userIds?: string[];
  fromCreatedTime?: number; // Search users created after this time (epoch in milliseconds)
  toCreatedTime?: number; // Search users created before this time (epoch in milliseconds)
  fromModifiedTime?: number; // Search users modified after this time (epoch in milliseconds)
  toModifiedTime?: number; // Search users modified before this time (epoch in milliseconds)
  tenantRoleIds?: Record<string, RolesList>; // Search users based on tenants and role IDs
  tenantRoleNames?: Record<string, RolesList>; // Search users based on tenants and role names
  verifiedEmail?: boolean; // Filter by verified email status
  verifiedPhone?: boolean; // Filter by verified phone status
};

type SingleUserResponse = {
  user: UserResponse;
};

type MultipleUsersResponse = {
  users: UserResponse[];
};

const withUser = (httpClient: HttpClient) => {
  /* Create User */
  function create(
    loginId: string,
    options?: UserOptions & {
      templateId?: string;
    },
  ): Promise<SdkResponse<UserResponse>>;
  function create(
    loginId: string,
    email?: string,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
    templateId?: string,
  ): Promise<SdkResponse<UserResponse>>;

  function create(
    loginId: string,
    emailOrOptions?: string | UserOptions,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
    templateId?: string,
  ): Promise<SdkResponse<UserResponse>> {
    // We support both the old and new parameters forms of create user
    // 1. The new form - create(loginId, { email, phone, ... }})
    // 2. The old form - create(loginId, email, phone, ...)
    const body =
      typeof emailOrOptions === 'string'
        ? {
            loginId,
            email: emailOrOptions,
            phone,
            displayName,
            givenName,
            middleName,
            familyName,
            roleNames: roles,
            userTenants,
            customAttributes,
            picture,
            verifiedEmail,
            verifiedPhone,
            additionalLoginIds,
            templateId,
          }
        : {
            loginId,
            ...emailOrOptions,
            roleNames: emailOrOptions?.roles,
            roles: undefined,
          };
    return transformResponse<SingleUserResponse, UserResponse>(
      httpClient.post(apiPaths.user.create, body),
      (data) => data.user,
    );
  }
  /* Create User End */

  /* Create Test User */
  function createTestUser(
    loginId: string,
    options?: UserOptions,
  ): Promise<SdkResponse<UserResponse>>;
  function createTestUser(
    loginId: string,
    email?: string,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
  ): Promise<SdkResponse<UserResponse>>;

  function createTestUser(
    loginId: string,
    emailOrOptions?: string | UserOptions,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
  ): Promise<SdkResponse<UserResponse>> {
    // We support both the old and new parameters forms of create test user
    // 1. The new form - createTestUser(loginId, { email, phone, ... }})
    // 2. The old form - createTestUser(loginId, email, phone, ...)
    const body =
      typeof emailOrOptions === 'string'
        ? {
            loginId,
            email: emailOrOptions,
            phone,
            displayName,
            givenName,
            middleName,
            familyName,
            roleNames: roles,
            userTenants,
            customAttributes,
            picture,
            verifiedEmail,
            verifiedPhone,
            additionalLoginIds,
            test: true,
          }
        : {
            loginId,
            ...emailOrOptions,
            roleNames: emailOrOptions?.roles,
            roles: undefined,
            test: true,
          };
    return transformResponse<SingleUserResponse, UserResponse>(
      httpClient.post(apiPaths.user.createTestUser, body),
      (data) => data.user,
    );
  }
  /* Create Test User End */

  /* Invite User */
  /**
   * Create a new user and invite them to set up their credentials.
   * When loginIdOrUserId is a loginId, a new user is created if one doesn't already exist.
   * When loginIdOrUserId is a userId, the user must already exist — no new user is created,
   * and the invite is sent to the existing user (useful for re-inviting).
   */
  function invite(
    loginIdOrUserId: string,
    options?: UserOptions & {
      inviteUrl?: string;
      sendMail?: boolean; // send invite via mail, default is according to project settings
      sendSMS?: boolean; // send invite via text message, default is according to project settings
      templateOptions?: TemplateOptions;
      templateId?: string;
      locale?: string; // locale for the invite message
    },
  ): Promise<SdkResponse<UserResponse>>;
  function invite(
    loginIdOrUserId: string,
    email?: string,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    inviteUrl?: string,
    sendMail?: boolean, // send invite via mail, default is according to project settings
    sendSMS?: boolean, // send invite via text message, default is according to project settings
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
    templateId?: string,
    locale?: string, // locale for the invite message
  ): Promise<SdkResponse<UserResponse>>;

  function invite(
    loginIdOrUserId: string,
    emailOrOptions?: string | UserOptions,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    inviteUrl?: string,
    sendMail?: boolean, // send invite via mail, default is according to project settings
    sendSMS?: boolean, // send invite via text message, default is according to project settings
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
    templateId?: string,
    locale?: string, // locale for the invite message
  ): Promise<SdkResponse<UserResponse>> {
    // We support both the old and new parameters forms of invite user
    // 1. The new form - invite(loginIdOrUserId, { email, phone, ... }})
    // 2. The old form - invite(loginIdOrUserId, email, phone, ...)
    const body =
      typeof emailOrOptions === 'string'
        ? {
            loginId: loginIdOrUserId,
            email: emailOrOptions,
            phone,
            displayName,
            givenName,
            middleName,
            familyName,
            roleNames: roles,
            userTenants,
            invite: true,
            customAttributes,
            picture,
            verifiedEmail,
            verifiedPhone,
            inviteUrl,
            sendMail,
            sendSMS,
            additionalLoginIds,
            templateId,
            locale,
          }
        : {
            loginId: loginIdOrUserId,
            ...emailOrOptions,
            roleNames: emailOrOptions?.roles,
            roles: undefined,
            invite: true,
          };
    return transformResponse<SingleUserResponse, UserResponse>(
      httpClient.post(apiPaths.user.create, body),
      (data) => data.user,
    );
  }
  /* Invite User End */

  /* Update User */
  function update(
    loginIdOrUserId: string,
    options?: UserOptions,
  ): Promise<SdkResponse<UserResponse>>;
  function update(
    loginIdOrUserId: string,
    email?: string,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
  ): Promise<SdkResponse<UserResponse>>;

  function update(
    loginIdOrUserId: string,
    emailOrOptions?: string | UserOptions,
    phone?: string,
    displayName?: string,
    roles?: string[],
    userTenants?: AssociatedTenant[],
    customAttributes?: Record<string, AttributesTypes>,
    picture?: string,
    verifiedEmail?: boolean,
    verifiedPhone?: boolean,
    givenName?: string,
    middleName?: string,
    familyName?: string,
    additionalLoginIds?: string[],
  ): Promise<SdkResponse<UserResponse>> {
    // We support both the old and new parameters forms of update user
    // 1. The new form - update(loginIdOrUserId, { email, phone, ... })
    // 2. The old form - update(loginIdOrUserId, email, phone, ...)
    const body =
      typeof emailOrOptions === 'string'
        ? {
            loginId: loginIdOrUserId,
            email: emailOrOptions,
            phone,
            displayName,
            givenName,
            middleName,
            familyName,
            roleNames: roles,
            userTenants,
            customAttributes,
            picture,
            verifiedEmail,
            verifiedPhone,
            additionalLoginIds,
          }
        : {
            loginId: loginIdOrUserId,
            ...emailOrOptions,
            roleNames: emailOrOptions?.roles,
            roles: undefined,
          };
    return transformResponse<SingleUserResponse, UserResponse>(
      httpClient.post(apiPaths.user.update, body),
      (data) => data.user,
    );
  }
  /* Update User End */

  /**
   * Helper function to build patch request body from options
   * @param loginIdOrUserId The login ID or user ID of the user
   * @param options The fields to update
   */
  function buildPatchRequestBody(loginIdOrUserId: string, options: PatchUserOptions): any {
    const body = {
      loginId: loginIdOrUserId,
    } as any;

    if (options.email !== undefined) {
      body.email = options.email;
    }
    if (options.phone !== undefined) {
      body.phone = options.phone;
    }
    if (options.displayName !== undefined) {
      body.displayName = options.displayName;
    }
    if (options.givenName !== undefined) {
      body.givenName = options.givenName;
    }
    if (options.middleName !== undefined) {
      body.middleName = options.middleName;
    }
    if (options.familyName !== undefined) {
      body.familyName = options.familyName;
    }
    if (options.roles !== undefined) {
      body.roleNames = options.roles;
    }
    if (options.userTenants !== undefined) {
      body.userTenants = options.userTenants;
    }
    if (options.customAttributes !== undefined) {
      body.customAttributes = options.customAttributes;
    }
    if (options.picture !== undefined) {
      body.picture = options.picture;
    }
    if (options.verifiedEmail !== undefined) {
      body.verifiedEmail = options.verifiedEmail;
    }
    if (options.verifiedPhone !== undefined) {
      body.verifiedPhone = options.verifiedPhone;
    }
    if (options.ssoAppIds !== undefined) {
      body.ssoAppIds = options.ssoAppIds;
    }
    if (options.scim !== undefined) {
      body.scim = options.scim;
    }
    if (options.status !== undefined) {
      body.status = options.status;
    }
    if (options.additionalIdentifiers !== undefined) {
      body.additionalIdentifiers = options.additionalIdentifiers;
    }

    return body;
  }

  /**
   * Patches an existing user.
   * @param loginIdOrUserId The login ID or user ID of the user
   * @param options The fields to update. Only the provided ones will be updated.
   */
  function patch(
    loginIdOrUserId: string,
    options: PatchUserOptions,
  ): Promise<SdkResponse<UserResponse>> {
    const body = buildPatchRequestBody(loginIdOrUserId, options);

    return transformResponse<SingleUserResponse, UserResponse>(
      httpClient.patch(apiPaths.user.patch, body),
      (data) => data.user,
    );
  }

  /**
   * Patches multiple users in batch.
   * @param users Array of patch requests. Each entry must include either `loginIdOrUserId`
   *              or the deprecated `loginId` field to identify the user.
   */
  function patchBatch(
    users: PatchUserOptionsUsingIdentifier[],
  ): Promise<SdkResponse<PatchUserBatchResponse>> {
    const missing = users.find((user) => !user.loginIdOrUserId && !user.loginId);
    if (missing) {
      return Promise.reject(
        new Error('patchBatch: each user must have loginIdOrUserId or loginId'),
      );
    }
    const body = {
      users: users.map((user) =>
        buildPatchRequestBody(user.loginIdOrUserId ?? user.loginId!, user),
      ),
    };

    return transformResponse<PatchUserBatchResponse, PatchUserBatchResponse>(
      httpClient.patch(apiPaths.user.patchBatch, body),
      (data) => data,
    );
  }

  return {
    create,
    /**
     * Create a new test user.
     * The loginID is required and will determine what the user will use to sign in.
     * Make sure the login id is unique for test. All other fields are optional.
     *
     * You can later generate OTP, Magic link and enchanted link to use in the test without the need
     * of 3rd party messaging services.
     * Those users are not counted as part of the monthly active users
     * @returns The UserResponse if found, throws otherwise.
     */
    createTestUser,
    invite,
    inviteBatch: (
      users: User[],
      inviteUrl?: string,
      sendMail?: boolean, // send invite via mail, default is according to project settings
      sendSMS?: boolean, // send invite via text message, default is according to project settings
      templateOptions?: TemplateOptions,
      templateId?: string,
      locale?: string, // locale for the invite message
    ): Promise<SdkResponse<CreateOrInviteBatchResponse>> =>
      transformResponse<CreateOrInviteBatchResponse, CreateOrInviteBatchResponse>(
        httpClient.post(apiPaths.user.createBatch, {
          users: transformUsersForBatch(users),
          invite: true,
          inviteUrl,
          sendMail,
          sendSMS,
          templateOptions,
          templateId,
          locale,
        }),
        (data) => data,
      ),
    createBatch: (users: User[]): Promise<SdkResponse<CreateOrInviteBatchResponse>> =>
      transformResponse<CreateOrInviteBatchResponse, CreateOrInviteBatchResponse>(
        httpClient.post(apiPaths.user.createBatch, {
          users: transformUsersForBatch(users),
        }),
        (data) => data,
      ),
    deleteBatch: (userIds: string[]): Promise<SdkResponse<never>> =>
      transformResponse(httpClient.post(apiPaths.user.deleteBatch, { userIds })),
    update,
    patch,
    patchBatch,
    /**
     * Delete an existing user.
     * @param loginIdOrUserId The login ID or user ID of the user
     */
    delete: (loginIdOrUserId: string): Promise<SdkResponse<never>> =>
      transformResponse(httpClient.post(apiPaths.user.delete, { loginId: loginIdOrUserId })),
    /**
     * Delete an existing user by User ID.
     * @param userId The user ID can be found in the Subject (`sub`) claim
     * in the user's JWT.
     */
    deleteByUserId: (userId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse(httpClient.post(apiPaths.user.delete, { userId })),
    /**
     * Delete all test users in the project.
     */
    deleteAllTestUsers: (): Promise<SdkResponse<never>> =>
      transformResponse(httpClient.delete(apiPaths.user.deleteAllTestUsers)),
    load: (loginIdOrUserId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.get(apiPaths.user.load, {
          queryParams: { loginId: loginIdOrUserId },
        }),
        (data) => data.user,
      ),
    /**
     * Load an existing user by user ID. The ID can be found
     * on the user's JWT.
     * @param userId load a user by this user ID field
     * @returns The UserResponse if found, throws otherwise.
     */
    loadByUserId: (userId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.get(apiPaths.user.load, {
          queryParams: { userId },
        }),
        (data) => data.user,
      ),
    /**
     * Logout a user from all devices by the login ID
     * @param loginId logout user by login ID
     * @returns The UserResponse if found, throws otherwise.
     */
    logoutUser: (loginId: string): Promise<SdkResponse<never>> =>
      transformResponse(httpClient.post(apiPaths.user.logout, { loginId })),
    /**
     * Logout a user from all devices by user ID. The ID can be found
     * on the user's JWT.
     * @param userId Logout a user from all devices by this user ID field
     * @returns The UserResponse if found, throws otherwise.
     */
    logoutUserByUserId: (userId: string): Promise<SdkResponse<never>> =>
      transformResponse(httpClient.post(apiPaths.user.logout, { userId })),
    /**
     * loadUsers by their user IDs.
     * @param userIds list of user IDs to load
     * @param includeInvalidUsers optionally include disabled/expired users in the response
     * @returns An array of UserResponse found by the query
     */
    loadUsers: (
      userIds: string[],
      includeInvalidUsers?: boolean,
    ): Promise<SdkResponse<UserResponse[]>> =>
      transformResponse<MultipleUsersResponse, UserResponse[]>(
        httpClient.post(apiPaths.user.loadUsers, {
          userIds,
          includeInvalidUsers,
        }),
        (data) => data.users,
      ),
    /**
     * Search all users. Results can be filtered according to tenants and/or
     * roles, and also paginated used the limit and page parameters.
     * @deprecated Use search instead
     * @param tenantIds optional list of tenant IDs to filter by
     * @param roles optional list of roles to filter by
     * @param limit optionally limit the response, leave out for default limit
     * @param page optionally paginate over the response
     * @param testUsersOnly optionally filter only test users
     * @param withTestUser optionally include test users in search
     * @returns An array of UserResponse found by the query
     */
    searchAll: (
      tenantIds?: string[],
      roles?: string[],
      limit?: number,
      page?: number,
      testUsersOnly?: boolean,
      withTestUser?: boolean,
      customAttributes?: Record<string, AttributesTypes>,
      statuses?: UserStatus[],
      emails?: string[],
      phones?: string[],
    ): Promise<SdkResponse<UserResponse[]>> =>
      transformResponse<MultipleUsersResponse, UserResponse[]>(
        httpClient.post(apiPaths.user.search, {
          tenantIds,
          roleNames: roles,
          limit,
          page,
          testUsersOnly,
          withTestUser,
          customAttributes,
          statuses,
          emails,
          phones,
        }),
        (data) => data.users,
      ),
    searchTestUsers: (searchReq: SearchRequest): Promise<SdkResponse<UserSearchResponse>> =>
      transformResponse<UserSearchResponse, UserSearchResponse>(
        httpClient.post(apiPaths.user.searchTestUsers, {
          ...searchReq,
          withTestUser: true,
          testUsersOnly: true,
          roleNames: searchReq.roles,
          roles: undefined,
        }),
        (data) => ({ users: data.users, total: data.total }),
      ),
    search: (searchReq: SearchRequest): Promise<SdkResponse<UserSearchResponse>> =>
      transformResponse<UserSearchResponse, UserSearchResponse>(
        httpClient.post(apiPaths.user.search, {
          ...searchReq,
          roleNames: searchReq.roles,
          roles: undefined,
        }),
        (data) => ({ users: data.users, total: data.total }),
      ),
    /**
     * Get the provider token for the given login ID.
     * Only users that logged-in using social providers will have token.
     * Note: The 'Manage tokens from provider' setting must be enabled.
     * @param loginId the login ID of the user
     * @param provider the provider name (google, facebook, etc.).
     * @param providerTokenOptions optional, includes options for getting the provider token:
     *    withRefreshToken - include the refresh token in the response
     *    forceRefresh - force to refresh the token
     * @returns The ProviderTokenResponse of the given user and provider
     */
    getProviderToken: (
      loginId: string,
      provider: string,
      providerTokenOptions?: ProviderTokenOptions,
    ): Promise<SdkResponse<ProviderTokenResponse>> =>
      transformResponse<ProviderTokenResponse>(
        httpClient.get(apiPaths.user.getProviderToken, {
          queryParams: {
            loginId,
            provider,
            withRefreshToken: providerTokenOptions?.withRefreshToken ? 'true' : 'false',
            forceRefresh: providerTokenOptions?.forceRefresh ? 'true' : 'false',
          },
        }),
        (data) => data,
      ),
    activate: (loginIdOrUserId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateStatus, {
          loginId: loginIdOrUserId,
          status: 'enabled',
        }),
        (data) => data.user,
      ),
    deactivate: (loginIdOrUserId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateStatus, {
          loginId: loginIdOrUserId,
          status: 'disabled',
        }),
        (data) => data.user,
      ),
    updateLoginId: (loginId: string, newLoginId?: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateLoginId, { loginId, newLoginId }),
        (data) => data.user,
      ),
    updateEmail: (
      loginIdOrUserId: string,
      email: string,
      isVerified: boolean,
      failOnConflict?: boolean,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateEmail, {
          loginId: loginIdOrUserId,
          email,
          verified: isVerified,
          failOnConflict,
        }),
        (data) => data.user,
      ),
    updatePhone: (
      loginIdOrUserId: string,
      phone: string,
      isVerified: boolean,
      failOnConflict?: boolean,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updatePhone, {
          loginId: loginIdOrUserId,
          phone,
          verified: isVerified,
          failOnConflict,
        }),
        (data) => data.user,
      ),
    updateDisplayName: (
      loginIdOrUserId: string,
      displayName?: string,
      givenName?: string,
      middleName?: string,
      familyName?: string,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateDisplayName, {
          loginId: loginIdOrUserId,
          displayName,
          givenName,
          middleName,
          familyName,
        }),
        (data) => data.user,
      ),
    updatePicture: (loginIdOrUserId: string, picture: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updatePicture, { loginId: loginIdOrUserId, picture }),
        (data) => data.user,
      ),
    updateCustomAttribute: (
      loginIdOrUserId: string,
      attributeKey: string,
      attributeValue: AttributesTypes,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateCustomAttribute, {
          loginId: loginIdOrUserId,
          attributeKey,
          attributeValue,
        }),
        (data) => data.user,
      ),
    setRoles: (loginIdOrUserId: string, roles: string[]): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.setRole, { loginId: loginIdOrUserId, roleNames: roles }),
        (data) => data.user,
      ),
    addRoles: (loginIdOrUserId: string, roles: string[]): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.addRole, { loginId: loginIdOrUserId, roleNames: roles }),
        (data) => data.user,
      ),
    removeRoles: (loginIdOrUserId: string, roles: string[]): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.removeRole, { loginId: loginIdOrUserId, roleNames: roles }),
        (data) => data.user,
      ),
    addTenant: (loginIdOrUserId: string, tenantId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.addTenant, { loginId: loginIdOrUserId, tenantId }),
        (data) => data.user,
      ),
    removeTenant: (loginIdOrUserId: string, tenantId: string): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.removeTenant, { loginId: loginIdOrUserId, tenantId }),
        (data) => data.user,
      ),
    setTenantRoles: (
      loginIdOrUserId: string,
      tenantId: string,
      roles: string[],
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.setRole, {
          loginId: loginIdOrUserId,
          tenantId,
          roleNames: roles,
        }),
        (data) => data.user,
      ),
    addTenantRoles: (
      loginIdOrUserId: string,
      tenantId: string,
      roles: string[],
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.addRole, {
          loginId: loginIdOrUserId,
          tenantId,
          roleNames: roles,
        }),
        (data) => data.user,
      ),
    removeTenantRoles: (
      loginIdOrUserId: string,
      tenantId: string,
      roles: string[],
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.removeRole, {
          loginId: loginIdOrUserId,
          tenantId,
          roleNames: roles,
        }),
        (data) => data.user,
      ),
    addSSOapps: (
      loginIdOrUserId: string,
      ssoAppIds: string[],
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.addSSOApps, { loginId: loginIdOrUserId, ssoAppIds }),
        (data) => data.user,
      ),
    setSSOapps: (
      loginIdOrUserId: string,
      ssoAppIds: string[],
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.setSSOApps, { loginId: loginIdOrUserId, ssoAppIds }),
        (data) => data.user,
      ),
    removeSSOapps: (
      loginIdOrUserId: string,
      ssoAppIds: string[],
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.removeSSOApps, { loginId: loginIdOrUserId, ssoAppIds }),
        (data) => data.user,
      ),

    /**
     * Generate OTP for the given login ID of a test user.
     * Choose the selected delivery method for verification.
     * Returns the code for the login (exactly as it sent via Email, SMS, Voice call or WhatsApp)
     * This is useful when running tests and don't want to use 3rd party messaging services
     *
     * @param deliveryMethod optional DeliveryMethod
     * @param loginId login ID of a test user
     * @param loginOptions optional LoginOptions - can be provided to set custom claims to the generated jwt.
     * @returns GenerateOTPForTestResponse which includes the loginId and the OTP code
     */
    generateOTPForTestUser: (
      deliveryMethod: DeliveryMethodForTestUser,
      loginId: string,
      loginOptions?: LoginOptions,
    ): Promise<SdkResponse<GenerateOTPForTestResponse>> =>
      transformResponse<GenerateOTPForTestResponse>(
        httpClient.post(apiPaths.user.generateOTPForTest, {
          deliveryMethod,
          loginId,
          loginOptions,
        }),
        (data) => data,
      ),

    /**
     * Generate Magic Link for the given login ID of a test user.
     * Choose the selected delivery method for verification.
     * It returns the link for the login (exactly as it sent via Email)
     * This is useful when running tests and don't want to use 3rd party messaging services
     *
     * @param deliveryMethod optional DeliveryMethod
     * @param loginId login ID of a test user
     * @param uri optional redirect uri which will be used instead of any global configuration.
     * @param loginOptions optional LoginOptions - can be provided to set custom claims to the generated jwt.
     * @returns GenerateMagicLinkForTestResponse which includes the loginId and the magic link
     */
    generateMagicLinkForTestUser: (
      deliveryMethod: DeliveryMethodForTestUser,
      loginId: string,
      uri: string,
      loginOptions?: LoginOptions,
    ): Promise<SdkResponse<GenerateMagicLinkForTestResponse>> =>
      transformResponse<GenerateMagicLinkForTestResponse>(
        httpClient.post(apiPaths.user.generateMagicLinkForTest, {
          deliveryMethod,
          loginId,
          URI: uri,
          loginOptions,
        }),
        (data) => data,
      ),

    /**
     * Generate Enchanted Link for the given login ID of a test user.
     * It returns the link for the login (exactly as it sent via Email)
     * and pendingRef which is used to poll for a valid session
     * This is useful when running tests and don't want to use 3rd party messaging services
     *
     * @param loginId login ID of a test user
     * @param uri optional redirect uri which will be used instead of any global configuration.
     * @param loginOptions optional LoginOptions - can be provided to set custom claims to the generated jwt.
     * @returns GenerateEnchantedLinkForTestResponse which includes the loginId, the enchanted link and the pendingRef
     */
    generateEnchantedLinkForTestUser: (
      loginId: string,
      uri: string,
      loginOptions?: LoginOptions,
    ): Promise<SdkResponse<GenerateEnchantedLinkForTestResponse>> =>
      transformResponse<GenerateEnchantedLinkForTestResponse>(
        httpClient.post(apiPaths.user.generateEnchantedLinkForTest, {
          loginId,
          URI: uri,
          loginOptions,
        }),
        (data) => data,
      ),

    generateEmbeddedLink: (
      loginId: string,
      customClaims?: Record<string, any>,
      timeout?: number,
    ): Promise<SdkResponse<GenerateEmbeddedLinkResponse>> =>
      transformResponse<GenerateEmbeddedLinkResponse>(
        httpClient.post(apiPaths.user.generateEmbeddedLink, { loginId, customClaims, timeout }),
        (data) => data,
      ),

    generateSignUpEmbeddedLink: (
      loginId: string,
      user?: {
        name?: string;
        givenName?: string;
        middleName?: string;
        familyName?: string;
        phone?: string;
        email?: string;
      },
      emailVerified?: boolean,
      phoneVerified?: boolean,
      loginOptions?: LoginOptions,
      timeout?: number,
    ): Promise<SdkResponse<GenerateEmbeddedLinkResponse>> =>
      transformResponse<GenerateEmbeddedLinkResponse>(
        httpClient.post(apiPaths.user.generateSignUpEmbeddedLink, {
          loginId,
          user,
          emailVerified,
          phoneVerified,
          loginOptions,
          timeout,
        }),
        (data) => data,
      ),

    /**
     * Set temporary password for the given login ID of user.
     * Note: The password will automatically be set as expired.
     * The user will not be able to log-in with this password, and will be required to replace it on next login.
     * See also: expirePassword
     * @param loginId The login ID of the user
     * @param password The password to set for the user
     */
    setTemporaryPassword: (loginId: string, password: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.setTemporaryPassword, { loginId, password }),
        (data) => data,
      ),

    /**
     * Set password for the given login ID of user.
     * @param loginId The login ID of the user
     * @param password The password to set for the user
     */
    setActivePassword: (loginId: string, password: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.setActivePassword, { loginId, password }),
        (data) => data,
      ),

    /** Deprecated (user setTemporaryPassword instead)
     * Set password for the given login ID of user.
     * Note: The password will automatically be set as expired.
     * The user will not be able to log-in with this password, and will be required to replace it on next login.
     * See also: expirePassword
     * @param loginId The login ID of the user
     * @param password The password to set for the user
     */
    setPassword: (loginId: string, password: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.setPassword, { loginId, password }),
        (data) => data,
      ),

    /**
     * Expire password for the given login ID.
     * Note: user sign-in with an expired password, the user will get an error with code.
     * Use the `ResetPassword` or `ReplacePassword` methods to reset/replace the password.
     * @param loginId The login ID of the user
     */
    expirePassword: (loginId: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.expirePassword, { loginId }),
        (data) => data,
      ),

    /**
     * Removes all registered passkeys (WebAuthn devices) for the user with the given login ID.
     * Note: The user might not be able to login anymore if they have no other authentication
     * methods or a verified email/phone.
     * @param loginId The login ID of the user
     */
    removeAllPasskeys: (loginId: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.removeAllPasskeys, { loginId }),
        (data) => data,
      ),

    /**
     * Removes TOTP seed for the user with the given login ID.
     * Note: The user might not be able to login anymore if they have no other authentication
     * methods or a verified email/phone.
     * @param loginId The login ID of the user
     */
    removeTOTPSeed: (loginId: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.removeTOTPSeed, { loginId }),
        (data) => data,
      ),

    /**
     * Removes all recovery codes for the user with the given login ID or user ID.
     * Note: The user will no longer be able to sign in with any previously
     * generated recovery codes.
     * @param loginIdOrUserId The login ID or user ID of the user
     */
    removeRecoveryCodes: (loginIdOrUserId: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.removeRecoveryCodes, { loginId: loginIdOrUserId }),
        (data) => data,
      ),

    /**
     * Retrieve users' authentication history, by the given user's ids.
     * @param userIds The user IDs
     */
    history: (userIds: string[]): Promise<SdkResponse<UserHistoryResponse[]>> =>
      transformResponse<UserHistoryResponse[]>(
        httpClient.post(apiPaths.user.history, userIds),
        (data) => data,
      ),

    /**
     * Import users and their password hashes from a source system.
     * @param source The name of the source system (e.g. "Auth0")
     * @param users The exported users payload as a raw byte/string buffer
     * @param hashes The exported password hashes payload as a raw byte/string buffer
     * @param dryrun When true, validate the import without persisting any users
     * @returns The UserImportResponse with the imported users and any failures
     */
    import: (
      source: string,
      users?: string | Uint8Array,
      hashes?: string | Uint8Array,
      dryrun?: boolean,
    ): Promise<SdkResponse<UserImportResponse>> =>
      transformResponse<UserImportResponse, UserImportResponse>(
        httpClient.post(apiPaths.user.import, {
          source,
          users,
          hashes,
          dryrun: dryrun ?? false,
        }),
        (data) => data,
      ),

    /**
     * Update the recovery email for the given login ID or user ID.
     * @param loginIdOrUserId The login ID or user ID of the user
     * @param email The recovery email to set for the user
     * @param isVerified Whether the recovery email is verified
     */
    updateRecoveryEmail: (
      loginIdOrUserId: string,
      email: string,
      isVerified?: boolean,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateRecoveryEmail, {
          loginId: loginIdOrUserId,
          recoveryEmail: email,
          verified: isVerified,
        }),
        (data) => data.user,
      ),

    /**
     * Update the recovery phone for the given login ID or user ID.
     * @param loginIdOrUserId The login ID or user ID of the user
     * @param phone The recovery phone to set for the user
     * @param isVerified Whether the recovery phone is verified
     */
    updateRecoveryPhone: (
      loginIdOrUserId: string,
      phone: string,
      isVerified?: boolean,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateRecoveryPhone, {
          loginId: loginIdOrUserId,
          recoveryPhone: phone,
          verified: isVerified,
        }),
        (data) => data.user,
      ),

    /**
     * Update the given/middle/family names for the given login ID or user ID.
     * @param loginIdOrUserId The login ID or user ID of the user
     * @param givenName The given name to set for the user
     * @param middleName The middle name to set for the user
     * @param familyName The family name to set for the user
     */
    updateUserNames: (
      loginIdOrUserId: string,
      givenName?: string,
      middleName?: string,
      familyName?: string,
    ): Promise<SdkResponse<UserResponse>> =>
      transformResponse<SingleUserResponse, UserResponse>(
        httpClient.post(apiPaths.user.updateName, {
          loginId: loginIdOrUserId,
          givenName,
          middleName,
          familyName,
        }),
        (data) => data.user,
      ),

    /**
     * Get the custom attributes schema defined for the project.
     * @returns An array of CustomAttribute definitions
     */
    getCustomAttributes: (): Promise<SdkResponse<CustomAttribute[]>> =>
      transformResponse<{ data: CustomAttribute[] }, CustomAttribute[]>(
        httpClient.get(apiPaths.user.getCustomAttributes),
        (data) => data.data,
      ),

    /**
     * Create custom attributes in the project's user schema.
     * @param attributes The custom attribute definitions to create
     * @returns The updated array of CustomAttribute definitions
     */
    createCustomAttributes: (
      attributes: CustomAttribute[],
    ): Promise<SdkResponse<CustomAttribute[]>> =>
      transformResponse<{ data: CustomAttribute[] }, CustomAttribute[]>(
        httpClient.post(apiPaths.user.createCustomAttributes, { attributes }),
        (data) => data.data,
      ),

    /**
     * Delete custom attributes from the project's user schema by name.
     * @param names The names of the custom attributes to delete
     * @returns The updated array of CustomAttribute definitions
     */
    deleteCustomAttributes: (names: string[]): Promise<SdkResponse<CustomAttribute[]>> =>
      transformResponse<{ data: CustomAttribute[] }, CustomAttribute[]>(
        httpClient.post(apiPaths.user.deleteCustomAttributes, { names }),
        (data) => data.data,
      ),

    /**
     * Remove a single passkey (WebAuthn credential) for the user with the given login ID.
     * @param loginId The login ID of the user
     * @param credentialId The credential ID of the passkey to remove
     */
    removePasskey: (loginId: string, credentialId: string): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.removePasskey, { loginId, credentialId }),
        (data) => data,
      ),

    /**
     * List the passkeys (WebAuthn credentials) for the user with the given login ID.
     * @param loginId The login ID of the user
     * @returns An array of UserPasskey belonging to the user
     */
    listPasskeys: (loginId: string): Promise<SdkResponse<UserPasskey[]>> =>
      transformResponse<{ passkeys: UserPasskey[] }, UserPasskey[]>(
        httpClient.post(apiPaths.user.listPasskeys, { loginId }),
        (data) => data.passkeys,
      ),

    /**
     * List the trusted devices for the given login IDs or user IDs.
     * @param loginIdsOrUserIds The login IDs or user IDs of the users
     * @returns An array of UserTrustedDevice
     */
    listTrustedDevices: (loginIdsOrUserIds: string[]): Promise<SdkResponse<UserTrustedDevice[]>> =>
      transformResponse<{ devices: UserTrustedDevice[] }, UserTrustedDevice[]>(
        httpClient.post(apiPaths.user.listTrustedDevices, { identifiers: loginIdsOrUserIds }),
        (data) => data.devices,
      ),

    /**
     * Remove trusted devices for the given login ID or user ID.
     * @param loginIdOrUserId The login ID or user ID of the user
     * @param deviceIds The IDs of the trusted devices to remove
     */
    removeTrustedDevices: (
      loginIdOrUserId: string,
      deviceIds: string[],
    ): Promise<SdkResponse<never>> =>
      transformResponse<never>(
        httpClient.post(apiPaths.user.removeTrustedDevices, {
          identifier: loginIdOrUserId,
          deviceIds,
        }),
        (data) => data,
      ),
  };
};

export interface PatchUserOptions {
  email?: string;
  phone?: string;
  displayName?: string;
  roles?: string[];
  userTenants?: AssociatedTenant[];
  customAttributes?: Record<string, AttributesTypes>;
  picture?: string;
  verifiedEmail?: boolean;
  verifiedPhone?: boolean;
  givenName?: string;
  middleName?: string;
  familyName?: string;
  ssoAppIds?: string[];
  scim?: boolean;
  status?: UserStatus;
  additionalIdentifiers?: string[];
}

/** User options for batch patch operations, identifying the user by loginIdOrUserId or loginId */
export type PatchUserOptionsUsingIdentifier = PatchUserOptions &
  (
    | {
        loginIdOrUserId: string;
        /** @deprecated Use loginIdOrUserId instead */
        loginId?: string;
      }
    | {
        /** @deprecated Use loginIdOrUserId instead */
        loginId: string;
        loginIdOrUserId?: string;
      }
  );

export default withUser;
